import { Fragment, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setEnableMobileMenu } from '../../reducers/ThemeOptions';
import { buildContextualNav, ADMIN_NAV } from '../../orbit/nav';
import { matchPermissions } from '../../orbit/rbac/useHasPermission';
import { useMe } from '../../orbit/me/MeProvider';
import { useBrand } from '../../orbit/brand/BrandProvider';

/**
 * Orbit-Sidebar — Permission-gated.
 * Sections:
 *  - "Applications" (kontextuell auf aktive Brand) — nur sichtbar wenn ein
 *    brandSlug in der URL ist und der Operator min. eine Permission der
 *    Section hat.
 *  - "Administration" (system-level) — gefiltert über `appSettings.read` /
 *    `integrations.read`.
 */

const SubMenu = ({ item, toggleMobileSidebar, permissions }) => {
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const location = useLocation();

  const visibleChildren = (item.children ?? []).filter((c) =>
    matchPermissions(permissions, { perms: c.perms, anyOf: c.anyOf }),
  );
  const hasSubmenu = visibleChildren.length > 0;

  const toggleSubMenu = (e) => {
    if (!item.to || hasSubmenu) {
      e.preventDefault();
      e.stopPropagation();
      setIsSubMenuOpen(!isSubMenuOpen);
    } else if (item.to) {
      toggleMobileSidebar();
    }
  };

  const isActive =
    location.pathname === item.to ||
    (hasSubmenu && visibleChildren.some((child) => child.to === location.pathname));

  const linkTo = item.to ?? (hasSubmenu ? '#' : '#');

  return (
    <li className={`metismenu-item ${isActive ? 'active' : ''}`}>
      <Link
        to={linkTo}
        className={`metismenu-link ${isActive ? 'active' : ''}`}
        onClick={toggleSubMenu}
      >
        <i className={`metismenu-icon ${item.icon}`} />
        {item.label}
        {hasSubmenu && (
          <i className={`metismenu-state-icon pe-7s-angle-${isSubMenuOpen ? 'up' : 'down'}`} />
        )}
      </Link>
      {hasSubmenu && (
        <ul className={`metismenu-container ${isSubMenuOpen ? 'visible' : ''}`}>
          {visibleChildren.map((child) => (
            <li key={child.to} className="metismenu-item">
              <Link
                to={child.to}
                className={`metismenu-link ${
                  location.pathname === child.to ? 'active' : ''
                }`}
                onClick={toggleMobileSidebar}
              >
                {child.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

const Nav = () => {
  const enableMobileMenu = useSelector((s) => s.ThemeOptions.enableMobileMenu);
  const dispatch = useDispatch();
  const me = useMe();
  const brand = useBrand();
  const { brandSlug: paramSlug } = useParams();

  const toggleMobileSidebar = () => {
    if (enableMobileMenu) dispatch(setEnableMobileMenu(false));
  };

  const slug = paramSlug ?? brand.currentBrand?.slug;
  const contextualItems = slug ? buildContextualNav(slug) : [];

  const filterItems = (items) =>
    items.filter(
      (item) =>
        matchPermissions(me.permissions, { perms: item.perms, anyOf: item.anyOf }) ||
        // Wenn der Item selbst keine Perms hat, aber sichtbare Kinder hat:
        (item.children ?? []).some((c) =>
          matchPermissions(me.permissions, { perms: c.perms, anyOf: c.anyOf }),
        ),
    );

  const renderMenu = (items) =>
    filterItems(items).map((item) => (
      <SubMenu
        key={item.label}
        item={item}
        toggleMobileSidebar={toggleMobileSidebar}
        permissions={me.permissions}
      />
    ));

  const visibleContextual = filterItems(contextualItems);
  const visibleAdmin = filterItems(ADMIN_NAV);

  return (
    <Fragment>
      <div className="vertical-nav-menu">
        {visibleContextual.length > 0 && (
          <>
            <h5 className="app-sidebar__heading">Applications</h5>
            <ul className="metismenu-container">{renderMenu(contextualItems)}</ul>
          </>
        )}

        {visibleAdmin.length > 0 && (
          <>
            <h5 className="app-sidebar__heading">Administration</h5>
            <ul className="metismenu-container">{renderMenu(ADMIN_NAV)}</ul>
          </>
        )}
      </div>
    </Fragment>
  );
};

export default Nav;
